<template>
  <section class="px-6 pt-5.5 pb-6">
    <div class="flex flex-col gap-4 mb-4.5 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
      <div>
        <h3 class="mb-0.5 text-base font-bold text-foreground">
          Connect an account
        </h3>
        <p class="text-xs font-medium text-muted-foreground">
          Banks, brokerages, custodians and cap tables sync on their own. Read-only.
        </p>
      </div>
      <DSInputGroup class="shrink-0 w-full sm:w-80">
        <DSInputGroupAddon>
          <Search class="size-5" />
        </DSInputGroupAddon>
        <DSInputGroupInput
          v-model="query"
          type="search"
          placeholder="Find institution…"
          aria-label="Find institution"
        />
      </DSInputGroup>
    </div>

    <!--
      The grid scrolls, not the dialog. At the design's five columns the ten
      default institutions are exactly two rows and 64 is tall enough to hold
      them without a scrollbar; narrower breakpoints wrap to four or five rows
      and scroll, as does a longer search result. Without this the panel grows
      past DialogContent's 90vh and takes the whole dialog into a scroll,
      pushing the composer out of view — and the two halves are meant to be a
      visible either/or.

      `p-1 -m-1` insets the scroll box by 4px so a tile's 3px focus ring is not
      clipped at the edges; the sibling margins above and below absorb it, which
      is why they are 4px larger than the 14px they render as.
    -->
    <div
      class="max-h-64 overflow-y-auto p-1 -m-1"
      :aria-busy="isLoading"
    >
      <!--
        Skeletons only when there is nothing to keep. With results already on
        screen the grid stays put and just dims, so typing never collapses the
        panel and never reflows the dialog.
      -->
      <ul
        v-if="isLoading && !institutions.length"
        class="grid grid-cols-1 gap-2.5 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"
      >
        <li
          v-for="index in SKELETON_COUNT"
          :key="index"
          class="min-w-0"
        >
          <InstitutionCardSkeleton />
        </li>
      </ul>
      <ul
        v-else-if="institutions.length"
        class="grid grid-cols-1 gap-2.5 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"
        :class="isLoading && 'opacity-60 transition-opacity duration-ds-fast ease-ds-standard'"
      >
        <li
          v-for="institution in institutions"
          :key="institution.institution_id"
          class="min-w-0"
        >
          <InstitutionCard
            :institution="institution"
            :disabled="disabled || isLoading"
            class="w-full"
            @select="emit('connect', $event)"
          />
        </li>
      </ul>
      <p
        v-else
        class="py-6 text-center text-sm font-medium text-muted-foreground"
        role="status"
      >
        No institution matches “{{ searchedQuery }}”. Try a different name, or
        pick a connector under more options.
      </p>
    </div>

    <DSButton
      variant="ghost"
      :disabled="disabled"
      class="w-full mt-4.5 bg-accent text-accent-foreground hover:bg-accent-foreground/10"
      @click="emit('more-options')"
    >
      More options
    </DSButton>
  </section>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { debouncedRef } from '@vueuse/core'
import { Search } from '@lucide/vue'

import { InstatutionData } from '@/views/LinkedData/utils/types'

import { DEBOUNCE_DELAY } from '@/const'
import { DEFAULT_INSTITUTIONS } from '@/views/LinkedData/utils/const'

import { handleCatchedError } from '@/helpers/common'

import { useLinkedDataConnectorsStore } from '@/store/linkedData/connectors'

import { DSButton, DSInputGroup, DSInputGroupAddon, DSInputGroupInput } from '@ds'

import InstitutionCard from './InstitutionCard.vue'
import InstitutionCardSkeleton from './InstitutionCardSkeleton.vue'

/** Fills the two rows the grid shows at its default five columns. */
const SKELETON_COUNT = 10

defineProps<{
  /** Set while a connect handshake is in flight, so tiles cannot be double-fired. */
  disabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'connect', institution: InstatutionData): void
  (e: 'more-options'): void
}>()

const linkedDataConnectorsStore = useLinkedDataConnectorsStore()

const query = ref('')
const debouncedQuery = debouncedRef(query, DEBOUNCE_DELAY)

// Empty search shows the curated default set — the same list, and the same
// server-side search behind it, that the legacy connect dialog uses.
const institutions = ref<InstatutionData[]>(DEFAULT_INSTITUTIONS)
const isSearching = ref(false)

/** The term the visible results belong to — what the empty state names. */
const searchedQuery = ref('')

const controller = ref<AbortController>()

/*
 * Every request carries a sequence number and only the newest one is allowed to
 * touch state.
 *
 * Aborting an in-flight request rejects it, and the store rethrows that as a
 * plain Error indistinguishable from a real failure — so the old code ran its
 * catch, blanked the list and flashed "No institution matches" a moment before
 * the newer response arrived. Comparing ids is robust whichever way the store
 * reports a cancellation.
 */
let latestRequestId = 0

/*
 * Loading covers the debounce gap too. Between a keystroke and the request
 * actually firing, results on screen are already stale; treating that as idle
 * is what let a stale empty state sit there looking like an answer.
 */
const isLoading = computed(
  () => isSearching.value || query.value.trim() !== debouncedQuery.value.trim(),
)

watch(debouncedQuery, async value => {
  controller.value?.abort()

  const requestId = ++latestRequestId

  if (!value) {
    institutions.value = DEFAULT_INSTITUTIONS
    searchedQuery.value = ''
    isSearching.value = false
    return
  }

  isSearching.value = true
  try {
    controller.value = new AbortController()
    const result = await linkedDataConnectorsStore.searchInstitutions(
      value,
      controller.value.signal,
    )
    if (requestId !== latestRequestId) return
    institutions.value = result
    searchedQuery.value = value
  } catch (e) {
    // Superseded: the rejection is our own abort, not a failure worth showing.
    if (requestId !== latestRequestId) return
    handleCatchedError(e as string)
    institutions.value = []
    searchedQuery.value = value
  } finally {
    if (requestId === latestRequestId) isSearching.value = false
  }
})

onBeforeUnmount(() => {
  // Nothing may land after teardown.
  latestRequestId++
  controller.value?.abort()
})
</script>
